import {
  For,
  Show,
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  onMount,
  untrack,
} from 'solid-js'

export interface CodeInputProps {
  value: string
  onValueChange: (value: string) => void
  onComplete?: (value: string) => void
  length?: number
  label?: string
  error?: string
  invalid?: boolean
  disabled?: boolean
  autofocus?: boolean
  name?: string
}

const digitsOnly = (raw: string) => raw.replace(/\D/g, '')

export function extractCode(pasted: string, length: number): string {
  const run = pasted.match(new RegExp(`\\d{${length}}`))
  if (run) {
    return run[0]
  }
  return digitsOnly(pasted).slice(0, length)
}

export function CodeInput(props: CodeInputProps) {
  const length = () => props.length ?? 6
  const inputId = createUniqueId()
  const errorId = createUniqueId()

  const [focused, setFocused] = createSignal(false)
  const [selection, setSelection] = createSignal<[number, number]>([0, 0])

  let inputRef: HTMLInputElement | undefined

  const code = createMemo(() => digitsOnly(props.value).slice(0, length()))
  const slots = createMemo(() => Array.from({ length: length() }, (_, i) => code()[i] ?? ''))
  const invalid = () => props.invalid === true || (props.error ?? '') !== ''

  const commit = (next: string) => {
    props.onValueChange(next)
    if (next.length === length()) {
      props.onComplete?.(next)
    }
  }

  const readSelection = () => {
    const el = inputRef
    if (!el) {
      return
    }
    setSelection([el.selectionStart ?? el.value.length, el.selectionEnd ?? el.value.length])
  }

  const moveCaretTo = (position: number) => {
    inputRef?.setSelectionRange(position, position)
    setSelection([position, position])
  }

  const activeSlot = createMemo(() => {
    const [start, end] = selection()
    if (start !== end) {
      return -1
    }
    return Math.min(start, length() - 1)
  })

  const isSelected = (index: number) => {
    const [start, end] = selection()
    return start !== end && index >= start && index < end
  }

  onMount(() => {
    if (props.autofocus) {
      inputRef?.focus()
    }
  })

  createEffect(() => {
    const filled = code().length
    const [start, end] = untrack(selection)
    if (start > filled || end > filled) {
      moveCaretTo(filled)
    }
  })

  const handleInput = (event: InputEvent & { currentTarget: HTMLInputElement }) => {
    const el = event.currentTarget
    const raw = el.value
    const next = digitsOnly(raw).slice(0, length())
    if (raw !== next) {
      const caret = Math.max(0, (el.selectionStart ?? raw.length) - (raw.length - next.length))
      el.value = next
      el.setSelectionRange(caret, caret)
    }
    readSelection()
    commit(next)
  }

  const handlePaste = (event: ClipboardEvent & { currentTarget: HTMLInputElement }) => {
    event.preventDefault()
    const pasted = extractCode(event.clipboardData?.getData('text') ?? '', length())
    if (pasted === '') {
      return
    }
    const el = event.currentTarget
    const [start, end] = [el.selectionStart ?? code().length, el.selectionEnd ?? code().length]
    const merged = (code().slice(0, start) + pasted + code().slice(end)).slice(0, length())
    el.value = merged
    moveCaretTo(Math.min(start + pasted.length, length()))
    commit(merged)
  }

  const handlePointerUp = () => {
    const el = inputRef
    if (!el) {
      return
    }
    if ((el.selectionEnd ?? 0) > (el.selectionStart ?? 0)) {
      readSelection()
      return
    }
    moveCaretTo(el.value.length)
  }

  const slotClass = (index: number) => {
    const isActive = focused() && activeSlot() === index
    const base =
      'flex flex-1 min-w-0 h-14 items-center justify-center rounded-[var(--radius-12)] border text-[length:var(--text-2xl)] font-[var(--font-semibold)] text-[color:var(--color-text)] tabular-nums transition-[border-color,box-shadow,background-color] duration-[var(--duration-normal)] ease-[var(--ease-component)]'
    const background = isSelected(index)
      ? 'bg-[color-mix(in_srgb,var(--color-primary-500)_14%,var(--color-surface))]'
      : 'bg-[var(--color-surface)]'
    if (invalid()) {
      return `${base} ${background} border-[var(--color-error)] ${isActive ? 'outline-2 -outline-offset-2 outline-[var(--color-error-600)]' : ''}`
    }
    if (isActive) {
      return `${base} ${background} border-[var(--color-primary-400)] shadow-[var(--focus-ring)]`
    }
    return `${base} ${background} border-[var(--color-field-border)]`
  }

  return (
    <div class="flex flex-col gap-1.5">
      <Show when={props.label}>
        <label
          for={inputId}
          class="text-[length:var(--text-sm)] font-[var(--font-medium)] text-[color:var(--color-text)]"
        >
          {props.label}
        </label>
      </Show>

      <div class={`relative ${props.disabled ? 'opacity-50' : ''}`}>
        <div class="flex gap-2 sm:gap-2.5" aria-hidden="true">
          <For each={slots()}>
            {(digit, index) => (
              <div class={slotClass(index())}>
                <Show
                  when={digit !== ''}
                  fallback={
                    <Show when={focused() && activeSlot() === index()}>
                      <span class="ds-code-caret block h-6 w-[2px] rounded-full bg-[var(--color-primary-500)]" />
                    </Show>
                  }
                >
                  {digit}
                </Show>
              </div>
            )}
          </For>
        </div>

        <input
          ref={inputRef}
          id={inputId}
          name={props.name}
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          autocomplete="one-time-code"
          autocorrect="off"
          spellcheck={false}
          maxlength={length()}
          value={code()}
          disabled={props.disabled}
          aria-invalid={invalid()}
          aria-describedby={props.error ? errorId : undefined}
          class="absolute inset-0 h-full w-full cursor-text rounded-[var(--radius-12)] border-0 bg-transparent p-0 text-center text-[length:var(--text-2xl)] text-transparent caret-transparent outline-none selection:bg-transparent disabled:cursor-not-allowed"
          onInput={handleInput}
          onPaste={handlePaste}
          onPointerUp={handlePointerUp}
          onSelect={readSelection}
          onKeyUp={readSelection}
          onFocus={() => {
            setFocused(true)
            moveCaretTo(code().length)
          }}
          onBlur={() => setFocused(false)}
        />
      </div>

      <Show when={props.error}>
        <p id={errorId} class="text-[length:var(--text-sm)] text-[color:var(--color-error)]">
          {props.error}
        </p>
      </Show>
    </div>
  )
}
